import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { C } from "../lib/colors.js";
import { surface, ui } from "../lib/telemetry/instrument.js";
import { tipTextParts } from "../lib/onboarding/tipEngine.js";
import { placeTip } from "../lib/onboarding/tipPlacement.js";
import { Button } from "./ui/Primitives.jsx";

const ARROW = 7;

/** The arrow's offset and drawn border, pointing from the card's `pos.side` toward the anchor. */
function arrowStyle(pos) {
  const clear = `${ARROW}px solid transparent`;
  const solid = `${ARROW}px solid ${C.accent}`;
  const across = { borderLeft: clear, borderRight: clear, left: pos.arrowX - ARROW };
  const along = { borderTop: clear, borderBottom: clear, top: pos.arrowY - ARROW };
  if (pos.side === "below") return { ...across, top: -ARROW, borderBottom: solid };
  if (pos.side === "above") return { ...across, bottom: -ARROW, borderTop: solid };
  if (pos.side === "right") return { ...along, left: -ARROW, borderRight: solid };
  return { ...along, right: -ARROW, borderLeft: solid };
}

/**
 * The first-contact tip on screen (hooks/useOnboardingTips.js), pinned beside its anchor.
 * Rendered into document.body so the app's display-size zoom does not offset it from the
 * anchor's viewport rect.
 *
 * @param {{tip: {id: string, text: string, rect: object, beside: boolean, above: boolean}|null,
 *   onDismiss: () => void, onTurnOff: () => void}} props
 */
export default function TipLayer({ tip, onDismiss, onTurnOff }) {
  const cardRef = useRef(null);
  const [cardH, setCardH] = useState(96);
  // Presses on the tip stay on the tip. Drawers and menus close on a mousedown that reaches the
  // document from outside them, and the tip is drawn outside them (a portal), so otherwise
  // "Got it" would close the panel the tip points into and never be clicked.
  const tipId = tip?.id ?? null;
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const stop = (e) => e.stopPropagation();
    // telemetry-gesture: keeps a press on a tip from closing the panel under it (no operation)
    el.addEventListener("mousedown", stop);
    el.addEventListener("touchstart", stop, { passive: true });
    return () => {
      el.removeEventListener("mousedown", stop);
      el.removeEventListener("touchstart", stop);
    };
  }, [tipId]);
  useLayoutEffect(() => {
    const h = cardRef.current?.offsetHeight;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (h && h !== cardH) setCardH(h);
  }, [tip, cardH]);
  if (!tip || typeof document === "undefined") return null;
  const pos = placeTip(tip.rect, cardH, {
    vw: window.innerWidth,
    vh: window.innerHeight,
    beside: tip.beside,
    above: tip.above,
  });
  // A beside tip whose room went (the window shrank) hides rather than cover its dialog.
  if (!pos) return null;
  const arrow = pos.side !== "inside" && (
    <span
      aria-hidden="true"
      style={{ position: "absolute", width: 0, height: 0, ...arrowStyle(pos) }}
    />
  );
  return createPortal(
    <div
      ref={cardRef}
      data-tip-layer=""
      role="status"
      aria-live="polite"
      {...surface("tip", tip.id)}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        width: pos.width,
        zIndex: 1500,
        boxSizing: "border-box",
        padding: "12px 14px 10px",
        background: C.panel,
        color: C.white,
        border: `1px solid ${C.accent}`,
        borderRadius: 10,
        boxShadow: C.shadowBox,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      {arrow}
      <div>
        {tipTextParts(tip.text).map((p, i) =>
          p.bold ? (
            <strong key={i} style={{ fontWeight: 600 }}>
              {p.text}
            </strong>
          ) : (
            <span key={i}>{p.text}</span>
          ),
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 10,
        }}
      >
        <button
          type="button"
          {...ui("tip.turn-off")}
          onClick={onTurnOff}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: C.grayL,
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          Turn off tips
        </button>
        <Button size="sm" variant="primary" {...ui("tip.dismiss")} onClick={onDismiss}>
          Got it
        </Button>
      </div>
    </div>,
    document.body,
  );
}
